// Service-token seam: exchanges the Better Auth browser session for a signed
// @aod/auth service token via the gateway's POST /auth/token bridge.
//
// The token's `sub` is the Better Auth user id and is the identity the
// backend services use for queue joins and ratings. Like session.ts, this
// degrades gracefully: any failure returns null and the UI falls back to
// anonymous (quickplay-only) play.
//
// Tokens are cached in-module until 60 s before expiry, so re-entering the
// multiplayer screen doesn't re-hit the bridge.

export interface ServiceToken {
  readonly token: string;
  readonly sub: string;
  /** Unix seconds at which the token expires. */
  readonly expiresAt: number;
}

type FetchLike = (input: string, init?: RequestInit) => Promise<Response>;

/** Normalised gateway HTTP origin (no trailing slash), or null when unset. */
export function resolveGatewayHttpUrl(raw: string | undefined | null): string | null {
  const value = raw?.trim();
  if (!value) return null;
  try {
    // Accept a ws(s):// gateway URL and translate, or a plain http(s) origin.
    const httpUrl = value.replace(/^ws/, 'http');
    const url = new URL(httpUrl);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    return url.href.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

export function getGatewayHttpUrl(): string | null {
  return resolveGatewayHttpUrl(import.meta.env?.VITE_GATEWAY_URL);
}

let cached: ServiceToken | null = null;
let inflight: Promise<ServiceToken | null> | null = null;

/** 60 s early-expiry margin so an in-flight match start never rides an expiring token. */
function isFresh(t: ServiceToken, nowSec: number): boolean {
  return t.expiresAt - 60 > nowSec;
}

export function clearServiceTokenCache(): void {
  cached = null;
  inflight = null;
}

export async function exchangeSessionForToken(
  gatewayHttpUrl: string,
  fetchImpl: FetchLike = fetch,
): Promise<ServiceToken | null> {
  const nowSec = Math.floor(Date.now() / 1000);
  if (cached && isFresh(cached, nowSec)) return cached;
  // Collapse concurrent exchanges onto one request.
  if (inflight) return inflight;

  inflight = (async () => {
    try {
      const res = await fetchImpl(`${gatewayHttpUrl}/auth/token`, {
        method: 'POST',
        credentials: 'include',
        headers: { accept: 'application/json' },
      });
      if (!res.ok) return null;
      const body = (await res.json()) as {
        token?: string;
        sub?: string;
        expiresAt?: number;
      };
      if (!body.token || !body.sub || typeof body.expiresAt !== 'number') return null;
      cached = { token: body.token, sub: body.sub, expiresAt: body.expiresAt };
      return cached;
    } catch {
      return null;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}
